import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { View } from '@actual-app/components/view';
import { css } from '@emotion/css';

import { IDIOMAS_RUMBO, NOMBRES_IDIOMAS } from '#i18n';

import { ATARDECER } from './PuertoAtardecer';

/**
 * Dibujadas, no emoji: en Windows un emoji de bandera sale como dos letras.
 * El catalán lleva la senyera.
 */
const BANDERAS: Record<string, ReactNode> = {
  es: (
    <svg viewBox="0 0 26 18" width={26} height={18} aria-hidden>
      <rect width="26" height="18" fill="#c8102e" />
      <rect y="4.5" width="26" height="9" fill="#ffc400" />
    </svg>
  ),
  ca: (
    <svg viewBox="0 0 26 18" width={26} height={18} aria-hidden>
      <rect width="26" height="18" fill="#fcdd09" />
      <g fill="#da121a">
        <rect y="2" width="26" height="2" />
        <rect y="6" width="26" height="2" />
        <rect y="10" width="26" height="2" />
        <rect y="14" width="26" height="2" />
      </g>
    </svg>
  ),
};

const estiloOpcion = css({
  flex: 1,
  minHeight: 48,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 9,
  padding: '0 10px',
  border: 0,
  borderRadius: 14,
  background: '#fff',
  color: ATARDECER.tinta,
  boxShadow: `inset 0 0 0 1.5px ${ATARDECER.linea}`,
  fontFamily: 'inherit',
  fontSize: 15,
  fontWeight: 800,
  cursor: 'pointer',
  '& svg': { borderRadius: 3, boxShadow: '0 0 0 1px rgba(0,0,0,.12)' },
  '&[aria-pressed="true"]': {
    background: '#e6f3ec',
    boxShadow: `inset 0 0 0 2px ${ATARDECER.verde}`,
  },
  ':focus-visible': {
    outline: `2px solid ${ATARDECER.verde}`,
    outlineOffset: 3,
  },
});

const estiloGrupo = css({
  width: '100%',
  maxWidth: 360,
  marginTop: 18,
  padding: 0,
  border: 0,
  minWidth: 0,
});

const estiloPregunta = css({
  width: '100%',
  padding: 0,
  marginBottom: 8,
  fontFamily: 'Fraunces, Georgia, "Times New Roman", serif',
  fontSize: 14,
  fontWeight: 700,
  color: ATARDECER.tinta2,
});

type SelectorIdiomaProps = {
  /** El idioma que se usa ahora. */
  activo: string | undefined;
  onElegir: (idioma: string) => void;
};

/** La primera pregunta de Rumbo: en qué idioma se navega. */
export function SelectorIdioma({ activo, onElegir }: SelectorIdiomaProps) {
  const { t } = useTranslation();
  const pregunta = t('Which language shall we sail in?');

  return (
    <fieldset className={estiloGrupo}>
      <legend className={estiloPregunta}>{pregunta}</legend>
      <View style={{ flexDirection: 'row', gap: 10 }}>
        {IDIOMAS_RUMBO.map(idioma => (
          <button
            key={idioma}
            type="button"
            className={estiloOpcion}
            aria-pressed={idioma === activo}
            onClick={() => onElegir(idioma)}
          >
            {BANDERAS[idioma]}
            <span lang={idioma}>{NOMBRES_IDIOMAS[idioma]}</span>
          </button>
        ))}
      </View>
    </fieldset>
  );
}
